import React, { useEffect, useState } from "react";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";
import { useTranslation } from "react-i18next";

const COOKIE_CONSENTIMIENTO = "tipifyCookieConsent";

// Consent Mode v2 (ver public/index.html): el banner solo cambia la analítica;
// publicidad sigue denegada porque la web no tiene anuncios de terceros.
const actualizarConsentimiento = (concedido) => {
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage: concedido ? "granted" : "denied",
  });
};

// Al rechazar se borran las cookies de GA que pudieran quedar de antes
// (hasta noviembre de 2026 GA medía aunque se rechazara)
const borrarCookiesGA = () => {
  const partes = window.location.hostname.split(".");
  const dominios = ["", window.location.hostname];
  if (partes.length > 1) dominios.push(`.${partes.slice(-2).join(".")}`);
  document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter((nombre) => nombre === "_ga" || nombre.startsWith("_ga_"))
    .forEach((nombre) => {
      dominios.forEach((dominio) => {
        document.cookie = `${nombre}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
          dominio ? `; domain=${dominio}` : ""
        }`;
      });
    });
};

// Rechazar y Aceptar con el mismo peso visual (la AEPD pide que rechazar sea
// tan fácil y visible como aceptar)
const BOTON = {
  background: "transparent",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.7)",
  borderRadius: "6px",
  fontSize: "12px",
  lineHeight: "1.2",
  padding: "7px 10px",
  margin: 0,
  minWidth: "72px",
  cursor: "pointer",
};

// Mientras la franja está abierta, --cookie-bar-h guarda su alto para que el
// contenido (padding del body) y los botones flotantes no queden debajo
const fijarAltoFranja = (alto) => {
  document.documentElement.style.setProperty("--cookie-bar-h", `${alto}px`);
};

const CookieConsentGlobal = () => {
  const { t } = useTranslation();
  const [abierta, setAbierta] = useState(
    () => getCookieConsentValue(COOKIE_CONSENTIMIENTO) === undefined,
  );

  // Quien rechazó en otra visita no debe conservar cookies de GA
  useEffect(() => {
    if (getCookieConsentValue(COOKIE_CONSENTIMIENTO) === "false") borrarCookiesGA();
  }, []);

  useEffect(() => {
    if (!abierta) {
      fijarAltoFranja(0);
      return undefined;
    }
    if (typeof ResizeObserver === "undefined") return undefined;
    // La librería pinta la franja un instante después de montar: se espera a
    // que exista y se sigue su alto (cambia con el idioma y el ancho)
    let resize = null;
    const seguir = (franja) => {
      fijarAltoFranja(franja.offsetHeight);
      resize = new ResizeObserver(() => fijarAltoFranja(franja.offsetHeight));
      resize.observe(franja);
    };
    const ya = document.querySelector(".CookieConsent");
    if (ya) {
      seguir(ya);
      return () => resize.disconnect();
    }
    const espera = new MutationObserver(() => {
      const franja = document.querySelector(".CookieConsent");
      if (!franja) return;
      espera.disconnect();
      seguir(franja);
    });
    espera.observe(document.body, { childList: true, subtree: true });
    return () => {
      espera.disconnect();
      if (resize) resize.disconnect();
    };
  }, [abierta]);

  return (
    <CookieConsent
      location="bottom"
      cookieName={COOKIE_CONSENTIMIENTO}
      onAccept={() => {
        actualizarConsentimiento(true);
        setAbierta(false);
      }}
      onDecline={() => {
        actualizarConsentimiento(false);
        borrarCookiesGA();
        setAbierta(false);
      }}
      buttonText={t("cookies.accept")}
      declineButtonText={t("cookies.reject")}
      enableDeclineButton
      expires={999}
      style={{
        background: "rgba(0,0,0,0.85)",
        zIndex: 1300,
        padding: "8px 12px calc(8px + env(safe-area-inset-bottom, 0px))",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: "8px",
      }}
      contentStyle={{
        flex: "1 1 auto",
        margin: 0,
        fontSize: "12px",
        lineHeight: "1.35",
      }}
      buttonWrapperClasses="cookie-bar-botones"
      buttonStyle={BOTON}
      declineButtonStyle={BOTON}
    >
      {t("cookies.short")}{" "}
      <a
        href="/politica-cookies"
        style={{ color: "#fff", textDecoration: "underline" }}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t("cookies.readMore")}
      </a>
    </CookieConsent>
  );
};

export default CookieConsentGlobal;
